import React, { useState } from 'react';
import { MainLayout } from '@/components/layouts/MainLayout';
import { EditableGrid } from '@/components/dashboard/EditableGrid';
import { DashboardWidget } from '@/components/dashboard/DashboardWidget';
import { EditModeToggle } from '@/components/dashboard/EditModeToggle';
import { WidgetConfigModal } from '@/components/dashboard/WidgetConfigModal';
import { AddWidgetButton } from '@/components/dashboard/AddWidgetButton';
import { useDashboardLayout } from '@/hooks/useDashboardLayout';
import { Button } from '@/components/ui/button';
import { Plus, Database, Upload, Download } from 'lucide-react';
import { CustomDataTableModal } from '@/components/statistics/CustomDataTableModal';
import { toast } from '@/hooks/use-toast';

export const Statistics: React.FC = () => {
  const {
    widgets,
    isEditMode,
    reorderWidgets,
    updateWidget,
    removeWidget,
    addWidget,
    toggleEditMode,
    cancelEdit,
    resetToDefault
  } = useDashboardLayout('statistics');

  const [showWidgetConfigModal, setShowWidgetConfigModal] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState<any>(null);
  const [showDataTableModal, setShowDataTableModal] = useState(false);
  const [editingTableData, setEditingTableData] = useState<any>(null);
  const [widgetWithOpenInsights, setWidgetWithOpenInsights] = useState<string | null>(null);

  const handleConfigure = (widget: any) => {
    setSelectedWidget(widget);
    setShowWidgetConfigModal(true);
  };

  const handleSaveWidgetConfig = (config: any) => {
    if (selectedWidget) {
      updateWidget({ ...selectedWidget, ...config });
    }
    setShowWidgetConfigModal(false);
    setSelectedWidget(null);
  };

  const handleAddDataTable = () => {
    setEditingTableData(null);
    setShowDataTableModal(true);
  };

  const handleEditDataTable = (widget: any) => {
    setSelectedWidget(widget);
    setEditingTableData(widget.config?.customData);
    setShowDataTableModal(true);
  };

  const handleSaveDataTable = (tableConfig: any) => {
    if (editingTableData && selectedWidget) {
      // Update existing widget
      updateWidget({
        ...selectedWidget,
        title: tableConfig.name,
        config: {
          ...selectedWidget.config,
          customData: tableConfig
        }
      });
    } else {
      // Create new widget
      addWidget('custom-table', {
        title: tableConfig.name,
        size: 'full',
        tableType: 'custom-data',
        customData: tableConfig
      });
    }
    setShowDataTableModal(false);
    setEditingTableData(null);
    setSelectedWidget(null);
  };

  const handleImportData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.json,.xlsx';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        let tableConfig: any = null;

        if (file.name.endsWith('.csv')) {
          tableConfig = parseCSV(text, file.name);
        } else if (file.name.endsWith('.json')) {
          tableConfig = parseJSON(text, file.name);
        }

        if (tableConfig) {
          handleSaveDataTable(tableConfig);
          toast({
            title: "Import réussi",
            description: `Le fichier "${file.name}" a été importé avec succès.`
          });
        }
      } catch (error) {
        toast({
          title: "Erreur d'import",
          description: "Impossible de lire le fichier. Vérifiez le format.",
          variant: "destructive"
        });
      }
    };
    input.click();
  };

  const parseCSV = (csvText: string, fileName: string) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return null;

    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    
    // Analyser les données pour détecter automatiquement les types
    const sampleRows = lines.slice(1, Math.min(6, lines.length)).map(line => 
      line.split(',').map(v => v.trim().replace(/"/g, ''))
    );

    const columns = headers.map((name, index) => {
      const sampleValues = sampleRows.map(row => row[index]).filter(val => val && val.trim());
      const detectedType = detectColumnType(sampleValues);
      
      return {
        id: (index + 1).toString(),
        name,
        type: detectedType
      };
    });

    const rows = lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
      const row: Record<string, any> = {};
      headers.forEach((_, index) => {
        row[(index + 1).toString()] = values[index] || '';
      });
      return row;
    });

    return {
      name: fileName.replace('.csv', ''),
      columns,
      rows
    };
  };

  const parseJSON = (jsonText: string, fileName: string) => {
    try {
      const data = JSON.parse(jsonText);
      
      // Si c'est un array d'objets
      if (Array.isArray(data) && data.length > 0) {
        const firstItem = data[0];
        const headers = Object.keys(firstItem);
        
        // Analyser les données pour détecter automatiquement les types
        const sampleValues = data.slice(0, Math.min(5, data.length));
        
        const columns = headers.map((name, index) => {
          const sampleColumnValues = sampleValues.map(item => String(item[name] || '')).filter(val => val.trim());
          const detectedType = detectColumnType(sampleColumnValues);
          
          return {
            id: (index + 1).toString(),
            name,
            type: detectedType
          };
        });

        const rows = data.map(item => {
          const row: Record<string, any> = {};
          headers.forEach((header, index) => {
            row[(index + 1).toString()] = String(item[header] || '');
          });
          return row;
        });

        return {
          name: fileName.replace('.json', ''),
          columns,
          rows
        };
      }
      
      // Si c'est un objet avec une structure de tableau
      if (data.columns && data.rows) {
        return {
          name: data.name || fileName.replace('.json', ''),
          columns: data.columns,
          rows: data.rows
        };
      }
      
      return null;
    } catch {
      return null;
    }
  };

  // Fonction pour détecter automatiquement le type de colonne
  const detectColumnType = (values: string[]): 'text' | 'number' | 'date' => {
    if (values.length === 0) return 'text';
    
    let numberCount = 0;
    let dateCount = 0;
    
    for (const value of values) {
      const cleanValue = value.trim();
      if (!cleanValue) continue;
      
      // Test si c'est un nombre
      if (!isNaN(Number(cleanValue)) && cleanValue !== '') {
        numberCount++;
      }
      
      // Test si c'est une date (formats courants)
      const dateFormats = [
        /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
        /^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY
        /^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY
        /^\d{4}\/\d{2}\/\d{2}$/, // YYYY/MM/DD
      ];
      
      if (dateFormats.some(format => format.test(cleanValue)) || !isNaN(Date.parse(cleanValue))) {
        dateCount++;
      }
    }
    
    const total = values.length;
    
    // Si plus de 70% des valeurs sont des nombres, c'est une colonne numérique
    if (numberCount / total > 0.7) {
      return 'number';
    }
    
    // Si plus de 70% des valeurs sont des dates, c'est une colonne de dates
    if (dateCount / total > 0.7) {
      return 'date';
    }
    
    // Sinon, c'est du texte
    return 'text';
  };

  const handleExportData = () => {
    if (widgets.length === 0) {
      toast({
        title: "Aucune donnée à exporter",
        description: "Créez d'abord des tableaux de données.",
        variant: "destructive"
      });
      return;
    }

    // Créer un CSV avec toutes les données des widgets
    let csvContent = "";
    
    widgets.forEach((widget, widgetIndex) => {
      if (widget.config?.customData) {
        const { name, columns, rows } = widget.config.customData;
        
        // Ajouter le nom du tableau
        if (widgetIndex > 0) csvContent += "\n\n";
        csvContent += `=== ${name} ===\n`;
        
        // Ajouter les en-têtes
        const headers = columns.map((col: any) => col.name).join(",");
        csvContent += headers + "\n";
        
        // Ajouter les données
        rows.forEach((row: any) => {
          const values = columns.map((col: any) => row[col.id] || "").join(",");
          csvContent += values + "\n";
        });
      }
    });

    // Télécharger le fichier CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `statistiques-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Export réussi",
      description: "Les données ont été exportées au format CSV."
    });
  };

  const statisticsActions = (
    <div className="flex items-center gap-2">
      <Button
        onClick={handleAddDataTable}
        variant="outline"
        size="sm"
        className="flex items-center gap-2"
      >
        <Database className="w-4 h-4" />
        Nouveau tableau
      </Button>
      <Button
        onClick={handleImportData}
        variant="outline"
        size="sm"
        className="flex items-center gap-2"
      >
        <Upload className="w-4 h-4" />
        Importer
      </Button>
      <Button
        onClick={handleExportData}
        variant="outline"
        size="sm"
        className="flex items-center gap-2"
      >
        <Download className="w-4 h-4" />
        Exporter
      </Button>
      <AddWidgetButton onAdd={addWidget} />
      <EditModeToggle
        isEditMode={isEditMode}
        onToggle={toggleEditMode}
        onCancel={cancelEdit}
      />
      {isEditMode && (
        <Button onClick={resetToDefault} variant="outline" size="sm">
          Réinitialiser
        </Button>
      )}
    </div>
  );

  return (
    <MainLayout title="Statistiques Personnalisées" actions={statisticsActions}>
      <div className="space-y-6">
        <EditableGrid
          widgets={widgets}
          onReorder={reorderWidgets}
          onConfigure={handleConfigure}
          onRemove={removeWidget}
          isEditMode={isEditMode}
          widgetWithOpenInsights={widgetWithOpenInsights}
        >
          {(widget) => (
            <DashboardWidget
              widget={widget}
              onEditProduct={undefined}
              managementMode="statistics"
              isEditMode={isEditMode}
              onEditDataTable={() => handleEditDataTable(widget)}
              onInsightsToggle={(isOpen) => {
                setWidgetWithOpenInsights(isOpen ? widget.id : null);
              }}
            />
          )}
        </EditableGrid>

        {widgets.length === 0 && !isEditMode && (
          <div className="text-center py-12">
            <Database className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Aucune statistique configurée</h3>
            <p className="text-muted-foreground mb-4">
              Commencez par créer votre premier tableau de données personnalisé ou importer un fichier
            </p>
            <div className="flex items-center gap-2 justify-center">
              <Button onClick={handleAddDataTable} className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Créer un tableau
              </Button>
              <Button onClick={handleImportData} variant="outline" className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Importer un fichier
              </Button>
            </div>
          </div>
        )}
      </div>

      <WidgetConfigModal
        isOpen={showWidgetConfigModal}
        onClose={() => {
          setShowWidgetConfigModal(false);
          setSelectedWidget(null);
        }}
        widget={selectedWidget}
        onSave={handleSaveWidgetConfig}
      />

      <CustomDataTableModal
        isOpen={showDataTableModal}
        onClose={() => {
          setShowDataTableModal(false);
          setEditingTableData(null);
          setSelectedWidget(null);
        }}
        onSave={handleSaveDataTable}
        initialData={editingTableData}
      />
    </MainLayout>
  );
};